// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

import React from 'react'
import {
  type PageResource,
  type CardResource,
  type ResourceAction,
  type PageResourceSection,
  initialPages
} from './Resource.types'
import { Card } from 'react-bootstrap'
import './ResourceTemplate.scss'

interface ActionItemProps {
  title: string
  action: ResourceAction
}

const ActionItem: React.FC<ActionItemProps> = ({ title, action }): JSX.Element => {
  if (action.type === 'links') {
    return (
      <span>
        {action.label}
        {action.actions?.map((e, idx) => (
          <React.Fragment key={idx}>
            <ActionItem title={action.label} action={e} />
            {action.actions && idx < action.actions.length - 1 && ', '}
          </React.Fragment>
        ))}
      </span>
    )
  } else {
    return (
      <a
        className={action.type === 'link' ? 'link' : `btn btn-${action?.variant ?? 'outline-primary'} btn-lg`}
        href={action.redirectTo}
        target={action.openInNewTab ? '_blank' : '_self'}
        rel={action.openInNewTab ? 'noreferrer' : undefined}
        style={action.style ?? undefined}
        intc-id={`${action.type}-${action.label}-${title}`.replaceAll(' ', '-')}
        data-wap_ref={`${action.type}-${action.label}-${title}`.replaceAll(' ', '-')}
        role={action.type}
        aria-label={`Go to ${action.redirectTo}`}
      >
        {action.label}
      </a>
    )
  }
}

interface ResourceTemplateProps {
  page: string
}

const ResourceTemplate: React.FC<ResourceTemplateProps> = (props): JSX.Element => {
  const page = props.page
  let resource: PageResource | undefined
  switch (page) {
    case 'ai':
      resource = initialPages.ai
      break
    case 'rd':
      resource = initialPages.rd
      break
    case 'cs':
      resource = initialPages.cs
      break
    case 'ev':
      resource = initialPages.ev
      break
    case 'showCase':
      resource = initialPages.showCase
      break
    case 'aiPosts':
      resource = initialPages.aiPosts
      break
    default:
      break
  }
  if (!resource) return <></>

  const renderSections = (sections: PageResourceSection[]): JSX.Element => {
    return (
      <div className="internal-container gap-s8">
        {sections.map((section: PageResourceSection, index: number) => (
          <React.Fragment key={index}>
            <span className="h3 resource-template-title-container pt-s4">{section.title}</span>
            {section.cards && renderCards(section.cards)}
          </React.Fragment>
        ))}
      </div>
    )
  }

  const renderCards = (cards: CardResource[]): JSX.Element => {
    return (
      <div className="resource-template-items-container gap-s8">
        {cards.map((card, index: number) => (
          <Card className="resource-template-item-container p-s0" key={index}>
            {!card.isVideo ? (
              <Card.Img
                variant="top"
                srcSet={card.imgSrcSet}
                src={card.imgSrc}
                alt={`${card.title} image`}
                loading="lazy"
              />
            ) : (
              <iframe
                className="card-img-top"
                src={card.imgSrc}
                title={card.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            )}

            <Card.Body>
              <div className="d-flex flex-column gap-s4 py-s6">
                <h3 className="h3">{card.title}</h3>
                {card?.text && <span>{card.text}</span>}
              </div>
              <div className="d-flex flex-column gap-s4 py-s4">
                {card.actions?.map((action: ResourceAction, actionIdx: number) => (
                  <ActionItem title={card.title} action={action} key={actionIdx} />
                ))}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="resource-template-container px-s8 py-s9 px-sm-s10 py-sm-s11">
      <div className="internal-container gap-s8">
        <div className="resource-template-title-container gap-s4">
          {resource.title && (
            <h2>
              <span className="display-4">{resource.title}</span>
            </h2>
          )}
          {resource.text && <span className="lead">{resource.text}</span>}
        </div>
        {resource.sections && renderSections(resource.sections)}
        {resource.cards && renderCards(resource.cards)}
        {resource?.actions && (
          <div className="d-flex flex-row justify-content-center w-100">
            <ActionItem title={resource.title} action={resource.actions} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ResourceTemplate
